import {gql} from '@apollo/client';

import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {MixtureMetadataStructuredTableRowMetadata} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  BulkFetchMixtureTypedValueQuery,
  BulkFetchMixtureTypedValueQueryVariables,
} from 'notebook/structured-tables/row-updaters/__generated/BulkFetchMixtureTypedValueQuery';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import doesColumnMatchTypeAndGroup from 'notebook/structured-tables/util/doesColumnMatchTypeAndGroup';
import isColumnForSchemaField from 'notebook/structured-tables/util/isColumnForSchemaField';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {
  convertTagToTypedValue,
  getTypedValueFromBioentitySummary,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {LiveCellValue} from '../LiveCellValues';
import updateCellWithLiveValue from '../util/updateCellWithLiveValue';

export const BULK_MIXTURE_TYPED_VALUE_DATA = gql`
  query BulkFetchMixtureTypedValueQuery($mixtureIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    mixtures(ids: $mixtureIds) {
      ... on MixtureNode {
        displayLabel
        tagsJson
        ...MixtureSpecificAttributesGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('MixtureSpecificAttributes').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type MixtureData = BulkFetchMixtureTypedValueQuery['mixtures'][number];

type MixtureInfo = ExtractByTypename<MixtureData, 'MixtureNode'>;

async function _bulkFetchModel(sampleLookupInfos: Array<SampleLookupInfo>): Promise<Array<MixtureData>> {
  const {mixtures} = await doQuerySafe<
    BulkFetchMixtureTypedValueQuery,
    BulkFetchMixtureTypedValueQueryVariables
  >(
    {query: BULK_MIXTURE_TYPED_VALUE_DATA},
    {
      variables: {
        mixtureIds: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  return mixtures;
}

const MixtureStructuredTableRowUpdater: StructuredTableRowUpdater<
  MixtureInfo,
  MixtureMetadataStructuredTableRowMetadata
> = {
  fetchModel(sampleLookupInfo: SampleLookupInfo): Promise<MixtureData> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(sampleLookupInfos: Array<SampleLookupInfo>): Promise<Array<MixtureData>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  updateCell(
    model: MixtureData | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (isInaccessible(model)) {
      return 'NO_VALUE';
    }
    switch (column.columnType) {
      case TableColumnType.EXISTING_BIOENTITY:
        // Update the BioentitySummary in case it's displayLabel has changed
        return getTypedValueFromBioentitySummary(model);

      case TableColumnType.MEASUREMENT_AMOUNT:
        return toStringTypedValue(model.amount);

      case TableColumnType.UNIT_SELECTOR:
        return toStringTypedValue(model.units);
      default: {
        const matchingTag = model.tagsJson.find(
          (tag: Tag) => isSchemaTag(tag) && isColumnForSchemaField(column, tag.tagSchemaField)
        );
        if (matchingTag) {
          return convertTagToTypedValue(matchingTag);
        }
        return 'NO_VALUE';
      }
    }
  },

  updateRow(
    model: MixtureData | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<MixtureMetadataStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<MixtureMetadataStructuredTableRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }

    // Post BNCH-16701, this updater may be used in many different table types so we should be cautious
    const existingBioentityColumnIndex = columns.findIndex((column) =>
      doesColumnMatchTypeAndGroup(column, TableColumnType.EXISTING_BIOENTITY, columnGroup)
    );

    if (existingBioentityColumnIndex === -1) {
      return row;
    }

    const {typedValue} = row.cells[existingBioentityColumnIndex];
    if (
      typedValue == null ||
      typedValue.type !== 'BIOENTITY' ||
      typedValue.bioentitySummary.id !== model.id
    ) {
      return row;
    }

    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];
      const liveValue = this.updateCell(model, columnGroup, column);
      return updateCellWithLiveValue(cell, liveValue);
    });

    return {
      ...row,
      cells,
    };
  },

  readFromCache({idForLookup: apiIdentifier}: SampleLookupInfo): MixtureInfo | null {
    const data = readQuerySafe<BulkFetchMixtureTypedValueQuery, BulkFetchMixtureTypedValueQueryVariables>(
      {query: BULK_MIXTURE_TYPED_VALUE_DATA},
      {variables: {mixtureIds: [apiIdentifier]}, fetchPolicy: 'cache-only'}
    );
    if (data == null || !data.mixtures?.[0] || isInaccessible(data.mixtures[0])) {
      return null;
    }
    return data.mixtures[0];
  },
};

export default MixtureStructuredTableRowUpdater;
